import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Loader2, KeyRound, Camera, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import PhotoUploadButton from "../PhotoUploadButton";

/**
 * Sprint 72 — Proof Action Dialog
 *
 * Single dialog driven by `target = { order, kind }`. Renders the right
 * UI for each proof kind:
 *   - pickup_otp           → agent enters customer's 6-digit OTP
 *   - pickup_handover      → agent uploads handover photo at origin hub
 *   - packing              → hub staff uploads packing photo
 *   - destination_arrival  → destination hub staff uploads arrival photo
 *
 * Caller invokes onDone() on success so the parent can refetch.
 */
export default function ProofActionDialog({ target, onClose, onDone }) {
  const [otp, setOtp] = useState("");
  const [photo, setPhoto] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState("");
  const order = target?.order;
  const kind = target?.kind;

  const reset = () => { setOtp(""); setNote(""); setPhoto(""); };

  const close = () => {
    reset();
    onClose && onClose();
  };

  const verifyPickupOtp = async () => {
    if (otp.trim().length < 4) {
      toast.error("Enter the 6-digit pickup OTP");
      return;
    }
    if (!photo) {
      toast.error("A pickup proof photo is required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/orders/${order.tracking_id}/verify-pickup-otp`, { otp: otp.trim(), photo_base64: photo });
      toast.success("Pickup verified! Order is now Picked Up.");
      onDone && onDone();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OTP verification failed");
    } finally { setSubmitting(false); }
  };

  const uploadPhoto = async (base64, endpoint, successMsg) => {
    setSubmitting(true);
    try {
      await api.post(`/orders/${order.tracking_id}/proof/${endpoint}`, {
        image_base64: base64,
        note: note.trim() || null,
      });
      toast.success(successMsg);
      onDone && onDone();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setSubmitting(false); }
  };

  if (!target) return null;

  const titles = {
    pickup_otp: "Verify Pickup OTP",
    pickup_handover: "Upload Handover Photo",
    packing: "Upload Packing Photo",
    destination_arrival: "Upload Destination Arrival Photo",
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="sm:max-w-md" data-testid={`proof-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {kind === "pickup_otp" ? <KeyRound className="h-5 w-5 text-orange-500" /> : <Camera className="h-5 w-5 text-emerald-500" />}
            {titles[kind] || "Proof of action"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Order <span className="font-mono">{order?.tracking_id}</span>
            {order?.pickup_address && <span className="block mt-1 text-slate-500 truncate">{order.pickup_address}</span>}
          </DialogDescription>
        </DialogHeader>

        {kind === "pickup_otp" && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Ask the customer for their <span className="font-semibold">6-digit Pickup OTP</span> and enter it below.
              You won&apos;t be able to mark the package picked up until this is verified.
            </p>
            <Input
              autoFocus
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              className="font-mono text-2xl text-center tracking-[0.5em] h-14"
              data-testid="pickup-otp-input"
            />
            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-slate-700 mb-2">Pickup proof photo (required)</p>
              <div className="flex items-center justify-center gap-2">
                <PhotoUploadButton
                  label={photo ? "Photo captured ✓" : "Take / choose photo"}
                  onUpload={setPhoto}
                  disabled={submitting}
                  testId="pickup-proof-photo"
                />
              </div>
            </div>
            <Button
              onClick={verifyPickupOtp}
              disabled={submitting || otp.length < 4 || !photo}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="pickup-otp-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & confirm pickup"}
            </Button>
          </div>
        )}

        {kind === "pickup_handover" && (
          <ProofPhotoForm
            instructions="Snap a photo of the package at the origin hub handover counter."
            note={note} setNote={setNote}
            onUpload={(b64) => uploadPhoto(b64, "pickup-handover", "Handover photo uploaded! Customer can see it now.")}
            submitting={submitting}
            testIdPrefix="pickup-handover"
          />
        )}

        {kind === "packing" && (
          <ProofPhotoForm
            instructions="Snap a photo of the package after packing. Customer sees this on their tracking page."
            note={note} setNote={setNote}
            onUpload={(b64) => uploadPhoto(b64, "packing", "Packing photo uploaded!")}
            submitting={submitting}
            testIdPrefix="packing"
          />
        )}

        {kind === "destination_arrival" && (
          <ProofPhotoForm
            instructions="Snap a photo of the package on arrival at the destination hub."
            note={note} setNote={setNote}
            onUpload={(b64) => uploadPhoto(b64, "destination-arrival", "Arrival photo uploaded!")}
            submitting={submitting}
            testIdPrefix="destination-arrival"
          />
        )}

        <button onClick={close} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600" data-testid="proof-dialog-close">
          <X className="h-4 w-4" />
        </button>
      </DialogContent>
    </Dialog>
  );
}

function ProofPhotoForm({ instructions, note, setNote, onUpload, submitting, testIdPrefix }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">{instructions}</p>
      <Input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Optional note (e.g. '2 boxes, sealed')"
        data-testid={`${testIdPrefix}-note`}
      />
      <div className="flex justify-center py-2">
        <PhotoUploadButton
          label={submitting ? "Uploading…" : "Take / choose photo"}
          onUpload={onUpload}
          disabled={submitting}
          testId={`${testIdPrefix}-photo`}
        />
      </div>
    </div>
  );
}
